import * as jwtDecode from 'jwt-decode'
import { IAuthJwtPayloadDTO } from '../types/user'
import { getCurrentUnixTime } from './index'
import Paths from './paths'
import { serverApi } from './request'
import { LOCAL_STORAGE_CURRENT_ORGANIZATION_KEY } from '../types'

export const LOCAL_STORAGE_ACCESS_TOKEN_KEY = 'accessToken'
export const LOCAL_STORAGE_REFRESH_TOKEN_KEY = 'refreshToken'
export const LOCAL_STORAGE_USER_TYPE = 'userType'

interface ITokenContainer {
  accessToken: string
  refreshToken: string
}

interface IParsedAccessJWT extends IAuthJwtPayloadDTO {
  exp: number
  iat: number
}

interface IParsedRefreshJWT {
  id: string
  iat: number
  exp: number
  jti: string
}

const refreshBefore: number = parseInt(process.env.REACT_APP_REFRESH_TOKEN_BEFORE_EXPIRED_IN_SEC || '900', 10)

const saveAuthTokens = (tokens: ITokenContainer, type?: string) => {
  localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, tokens.accessToken)
  localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, tokens.refreshToken)
  if (type) {
    localStorage.setItem(LOCAL_STORAGE_USER_TYPE, type)
  }
}

const removeAuthTokens = () => {
  localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)
  localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY)
  localStorage.removeItem(LOCAL_STORAGE_CURRENT_ORGANIZATION_KEY)
  localStorage.removeItem(LOCAL_STORAGE_USER_TYPE)
}

const setRefreshAuthTokenSender = (accessToken: string) => {
  try {
    const parsedAccessToken: IParsedAccessJWT = jwtDecode(accessToken)
    const currentTime = getCurrentUnixTime()
    const refreshAfter = Math.round(Math.abs(parsedAccessToken.exp - currentTime) - refreshBefore)
    setTimeoutToRefreshToken(refreshAfter * 1000)
  } catch (e) {
    return e
  }
}

const setTimeoutToRefreshToken = (sendAfterInMilliseconds: number) => {
  setTimeout(async () => {
    const authTokens = await refreshAuthToken()
    updateTokenAndSetNewTimeoutToRefresh(authTokens)
  }, sendAfterInMilliseconds)
}

const updateTokenAndSetNewTimeoutToRefresh = (tokenPairs: ITokenContainer) => {
  saveAuthTokens(tokenPairs)
  setRefreshAuthTokenSender(tokenPairs.accessToken)
}

const isTokenValid = (): boolean => {
  const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)
  if (!accessToken) {
    return false
  }

  try {
    const parsedAccessToken: IParsedAccessJWT = jwtDecode(accessToken)
    if (isDateExpired(parsedAccessToken.exp)) {
      if (isRefreshTokenValid()) {
        setRefreshAuthTokenSender(accessToken)
        return true
      }
      return false
    }
    return true
  } catch (e) {
    removeAuthTokens()
    return false
  }
}

const isRefreshTokenValid = (): boolean => {
  const refreshToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)
  if (!refreshToken) {
    return false
  }
  try {
    const parsedRefreshToken: IParsedRefreshJWT = jwtDecode(refreshToken)
    if (isDateExpired(parsedRefreshToken.exp)) {
      removeAuthTokens()
      return false
    }
    return true
  } catch (e) {
    removeAuthTokens()
    return false
  }
}

const initTokenRefresh = (): void => {
  if (isRefreshTokenValid()) {
    setRefreshAuthTokenSender(localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)!)
  }
}

const isDateExpired = (dateInUnix: number) => {
  return dateInUnix < getCurrentUnixTime()
}

const refreshAuthToken = async () => {
  const response = await serverApi.post<ITokenContainer>(Paths.authentication.refreshToken, {
    refreshToken: localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY)
  })
  return response.data
}

const getAccessJwtPayload = (): IParsedAccessJWT | null => {
  const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)
  if (!token) {
    return null
  }
  return jwtDecode(token)
}

function signOut() {
  removeAuthTokens()
  window.location.replace('/auth')
}

export {
  saveAuthTokens,
  setRefreshAuthTokenSender,
  setTimeoutToRefreshToken,
  isTokenValid,
  initTokenRefresh,
  getAccessJwtPayload,
  signOut
}



// WEBPACK FOOTER //
// ./src/utils/auth.ts